import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import {
  CloseCircleOutlined,
  CaretDownOutlined,
  PoweroffOutlined,
  UnlockOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import api from '@api/api';
import CustomTable from '@components/CustomTable/CustomTable';
import TextEllipsis from '@components/TextEllipsis/TextEllipsis';
import TitleBar from '@components/TitleBar/TitleBar';
import useCustomConfirmModal from '@hooks/useCustomConfirmModal';
import { CommonResponseObj } from '@models/common';
import ExtendedGlobalToken from '@models/extendedThemeToken';
import { ConnectorStatusEnum, StartChargeTypeEnum } from '@models/gun';
import {
  allSeries,
  CATALOG,
  DisableEnableGunField,
  partNumberMap,
  PileStatusEnum,
  RealtimePileInfo,
} from '@models/pile';
import { formatNumberWithDigits, formatDuration } from '@utils/common';
import { AUTO_UPDATE_INTERVAL } from '@utils/const';
import {
  Button,
  Card,
  Menu,
  Popover,
  App,
  theme,
  Tag,
  Typography,
  Dropdown,
} from 'antd';
import dayjs from 'dayjs';
import ReactECharts from 'echarts-for-react';

import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/lib/table';

import './RealTimePileDetail.scss';

const { Text } = Typography;
const { useToken } = theme;
type MenuItem = Required<MenuProps>['items'][number];

interface AlertList {
  id: number;
  pileNum: string;
  gunNoId: number;
  alarmName: string | null;
  alarmPlatformName: string | null;
  level: number | null;
  startTime: number;
}

interface AlertListPageConfig {
  pileIds: number[];
  alarmHandleStatus: 'Pending';
  pageSize: number;
  current: number;
  total: number;
}

const initialAlertListPageConfig: AlertListPageConfig = {
  pileIds: [],
  alarmHandleStatus: 'Pending',
  pageSize: 10,
  current: 1,
  total: 0,
};

const API = {
  GET_PILE_DETAIL: '/api/operation-service/monitor/pile',
  // GET_DISABLED_GUN_LIST:
  //   '/api/config-service/pileChargingThreshold/getPileDisableByPileId/',
  CHECK_GUN_CAN_EDIT: '/api/config-service/gun/gunStateCanEdit',
  DISABLE_GUN: '/api/config-service/gun/prohibitGunByPileIdAndGunIds',
  ENABLE_GUN: '/api/config-service/gun/allowGunByPileIdAndGunIds',
  UNLOCK_GUN: '/api/config-service/gun/unlockGunByPileIdAndGunIds',
  ALERT_LIST: '/api/config-service/alarmHis/pages',
  HARD_RESET_PILE: '/api/config-service/gun/hardresetPileByPileId',
  SOFT_RESET_PILE: '/api/config-service/gun/softresetPileByPileId',
};

interface GaugeChartProps {
  data: {
    maxValue: number;
    value: number;
  };
}

function GaugeChart({ data: { maxValue, value } }: GaugeChartProps) {
  const option = {
    series: [
      {
        radius: '100%',
        center: ['50%', '58%'],
        type: 'gauge',
        min: 0,
        max: maxValue,
        progress: {
          show: true,
          width: 20,
        },
        axisLine: {
          lineStyle: {
            width: 20,
            color: [[1, 'rgba(208, 213, 221, 1)']],
          },
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          length: 12,
          lineStyle: {
            width: 1,
            color: 'rgba(117, 129, 153, 1)',
          },
        },
        axisLabel: {
          distance: 32,
          color: 'rgba(117, 129, 153, 1)',
          fontSize: 14,
        },
        pointer: {
          length: '50%',
          width: 3,
        },
        anchor: {
          show: true,
          showAbove: true,
          size: 16,
          itemStyle: {
            borderWidth: 3,
            borderColor: 'rgba(47, 62, 115, 1)',
          },
        },
        title: {
          show: true,
        },
        detail: {
          show: false,
          // valueAnimation: true,
          // fontSize: 36,
          // offsetCenter: [0, '60%'],
          // color: 'rgba(47, 62, 115, 1)',
        },
        data: [
          {
            value,
            itemStyle: {
              color: 'rgba(47, 62, 115, 1)',
            },
            title: {
              color: 'rgba(117, 129, 153, 1)',
              fontSize: 16,
              fontWeight: 700,
              offsetCenter: [0, '25%'],
            },
          },
        ],
      },
    ],
  };
  return (
    <>
      <div className="RealtimePileMonitor__gaugeValue">
        {formatNumberWithDigits(value, 3)}
      </div>
      <ReactECharts
        option={option}
        style={{ height: '20rem', width: '20rem' }}
        opts={{ renderer: 'svg' }}
      />
    </>
  );
}

interface SocChartProps {
  percentage: number;
  color: string;
}
function SocChart({ percentage, color }: SocChartProps) {
  const option = {
    tooltip: {
      show: false, // Disables tooltip
    },
    legend: {
      show: false, // Hides the legend
    },
    series: [
      {
        type: 'pie',
        radius: ['84%', '100%'],
        avoidLabelOverlap: false,
        hoverAnimation: false, // Disable hover effects
        label: {
          show: false,
          position: 'center',
        },
        emphasis: {
          label: {
            show: false,
          },
        },
        labelLine: {
          show: false,
        },
        data: [
          {
            value: percentage,
            itemStyle: { color },
          },
          {
            value: 100 - percentage,
            itemStyle: { color: '#F2F4F7' },
          },
        ],
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: '13.5rem', width: '13.5rem' }}
      opts={{ renderer: 'svg' }}
    />
  );
}

function RealTimePileDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { stationId, pileId } = useParams();
  const { gunId }: { gunId: number | null } = location.state || { gunId: null };
  const { token } = useToken() as unknown as ExtendedGlobalToken;
  const { message } = App.useApp();
  const [confirmDisableOrEnable] = useCustomConfirmModal();
  const [confirmSoftReset] = useCustomConfirmModal();
  const [confirmHardReset] = useCustomConfirmModal();
  const [confirmUnlock] = useCustomConfirmModal();

  const [isDisableOrEnableLoading, setIsDisableOrEnableLoading] =
    useState(false);
  const [isUnlockLoading, setIsUnlockLoading] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);

  const [pileData, setPileData] = useState({
    gunInfos: null,
    pileMetadataVo: null,
    pileStatus: '',
  } as RealtimePileInfo);

  const [gunData, setGunData] = useState({
    currentGunId: gunId,
    gunIds: [],
  } as {
    currentGunId: number | null;
    gunIds: number[];
  });

  const [alertListPageConfig, setAlertListPageConfig] = useState(
    initialAlertListPageConfig,
  );
  const [alertList, setAlertList] = useState<AlertList[]>([]);
  const [isAlertListLoading, setIsAlertTableLoading] = useState(true);

  // useRef to store the timeout ID for auto-updates so it can be cleared on unmount
  const pileDetailTimeoutRef = useRef(undefined as any);
  const alertListTimeoutRef = useRef(undefined as any);
  const pileDetailAbortRef = useRef<AbortController | null>(null);
  const alertListAbortRef = useRef<AbortController | null>(null);
  // Create a ref to store the latest value of alertListPageConfig.current
  const alertListCurrentRef = useRef(alertListPageConfig.current);

  const currentGun = pileData.gunInfos?.find(
    (gun) => gun.gunId === gunData.currentGunId,
  );
  const currentGunIndexString = gunData.gunIds
    .findIndex((thisGunId) => thisGunId === gunData.currentGunId)
    .toString();

  // 擺在這邊是為了提早在沒有 stationId 的情況中立刻導向首頁
  useEffect(() => {
    if (!stationId) {
      navigate('/');
    }
  }, [stationId]);

  function getPartNumberSeriesName(pileTypeName: string | undefined) {
    if (!pileTypeName) return '-';

    const partNumberObj =
      partNumberMap[pileTypeName as keyof typeof partNumberMap];
    if (!partNumberObj) return '-';
    return t(allSeries[partNumberObj.series as keyof typeof allSeries].label);
  }

  const alertColumns: ColumnsType<AlertList> = [
    {
      key: 'startTime',
      title: t('Common.AlertTime'),
      dataIndex: 'startTime',
      width: '11.25rem',
      render: (startTime) => dayjs(startTime).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      key: 'level',
      title: t('Common.SeverityLevel'),
      dataIndex: 'level',
      align: 'center',
      width: '7.5rem',
      render: (level) => {
        if (level === null) return <>-</>;

        let alarmColor;
        if (level <= 3) {
          alarmColor = token.colorBorder;
        } else if (level <= 5) {
          alarmColor = '#F2CB00';
        } else {
          alarmColor = token.colorError;
        }
        return (
          <div
            className="RealtimePileMonitor__alertLevel"
            style={{ backgroundColor: alarmColor }}
          />
        );
      },
    },
    {
      key: 'alarmName',
      title: t('Common.AlertType'),
      dataIndex: 'alarmName',
      width: '15.375rem',
      render: (_, record) => (
        <TextEllipsis
          text={record.alarmName || record.alarmPlatformName || '-'}
        />
      ),
    },
    {
      key: 'pileNum',
      title: t('Common.PlatformPileHardwareNum'),
      dataIndex: 'pileNum',
      width: '15.75rem',
      render: (pileNum) => <TextEllipsis text={pileNum} />,
    },
    {
      key: 'gunNoId',
      title: t('Common.GunID'),
      dataIndex: 'gunNoId',
      render: (gunNoId) => String.fromCharCode(65 + parseInt(gunNoId, 10)),
      width: '15.375rem',
    },
    {
      key: 'action',
      title: t('RealTimePileDetail.RepairProgress'),
      width: '7.5rem',
      render: () => <Tag color="error">{t('RealTimePileDetail.Pending')}</Tag>,
    },
  ];

  async function getPileDetail(initialDelay = AUTO_UPDATE_INTERVAL) {
    // 取消上一個請求
    pileDetailAbortRef.current?.abort();

    const controller = new AbortController();
    pileDetailAbortRef.current = controller;

    let delay = initialDelay;

    try {
      const res = await api.get(`${API.GET_PILE_DETAIL}?pileId=${pileId}`, {
        signal: controller.signal,
      });

      const {
        code,
        data,
        msg,
      }: { code: number; data: RealtimePileInfo; msg: string } =
        res.data as CommonResponseObj;

      if (code !== 0) {
        message.error(msg || t('Common.OperationFailed'));
        throw new Error(msg);
      }

      if (data.gunInfos) {
        const sortedGunIds = data.gunInfos
          .map((gun) => gun.gunId)
          .sort((a, b) => a - b);
        setGunData((prev) => ({
          ...prev,
          currentGunId: prev.currentGunId ?? sortedGunIds[0],
          gunIds: sortedGunIds,
        }));
      }

      if (data?.gunInfos && data?.pileMetadataVo) {
        const formattedData = {
          ...data,
          pileMetadataVo: {
            ...data.pileMetadataVo,
            pileStatus: data.pileStatus,
          },
        } as RealtimePileInfo;

        setPileData(formattedData);
        delay = AUTO_UPDATE_INTERVAL; // 成功後恢復 5 秒
      }
    } catch (err) {
      console.warn(err, new Date());
      delay = Math.min(delay * 2, 60000); // 指數退避，最大 60 秒
    }
    pileDetailTimeoutRef.current = setTimeout(
      () => getPileDetail(delay),
      delay,
    );
  }

  async function getAlertList(page = 1, initialDelay = AUTO_UPDATE_INTERVAL) {
    if (!pileId) return;

    // 取消上一個請求
    alertListAbortRef.current?.abort();

    const controller = new AbortController();
    alertListAbortRef.current = controller;

    setIsAlertTableLoading(true);
    let delay = initialDelay;

    try {
      const payload = {
        pileIds: [Number(pileId)],
        alarmHandleStatus: 'Pending',
        pageSize: 10,
        current: page,
      };
      const res = await api.post(API.ALERT_LIST, payload, {
        signal: controller.signal,
      });

      const { code, data, msg }: CommonResponseObj = res.data;
      if (code !== 0) {
        message.error(msg || t('Common.OperationFailed'));
        throw new Error(msg);
      }
      const { totalCount, data: records } = data;
      setAlertList(records);
      setAlertListPageConfig((prevConfig) => ({
        ...prevConfig,
        current: page,
        total: totalCount,
      }));
      delay = AUTO_UPDATE_INTERVAL;
    } catch (err) {
      console.warn(err, new Date());
      delay = Math.min(delay * 2, 60000);
    } finally {
      setIsAlertTableLoading(false);
    }
    alertListTimeoutRef.current = setTimeout(
      () => getAlertList(alertListCurrentRef.current, delay),
      delay,
    );
  }

  function onChangeViewingGun(thisGunId: string) {
    setGunData((prevGunData) => ({
      ...prevGunData,
      currentGunId: parseInt(thisGunId, 10),
    }));
  }

  function generateGunTabs(): MenuItem[] {
    return gunData.gunIds.map((thisGunId, index) => ({
      label: `${t('RealTimePileDetail.Gun')} ${String.fromCharCode(
        65 + index,
      )}`,
      key: thisGunId.toString(),
    }));
  }

  async function checkGunCanEdit() {
    // 檢查使用者能不能和此槍互動;
    const canEditGunRes = await api.post(API.CHECK_GUN_CAN_EDIT, {
      pileId: Number(pileId),
      gunNumbers: [currentGunIndexString],
    });

    const { code, data }: CommonResponseObj = canEditGunRes.data;
    if (code !== 0 || !data) {
      message.error(t('RealTimePileDetail.RemoteControlProhibited'));
      throw new Error('無法遠端控制槍');
    }
  }

  async function handleDisableOrEnableGun() {
    if (pileId === undefined) return;
    setIsDisableOrEnableLoading(true);

    if (
      currentGun?.connectorStatus !== 'Available' &&
      currentGun?.connectorStatus !== 'Unavailable'
    )
      return;

    try {
      await checkGunCanEdit();
    } catch (err) {
      console.warn(err);
      setIsDisableOrEnableLoading(false);
      return;
    }

    if (currentGun.connectorStatus === 'Unavailable') {
      // 啟用
      const payload: DisableEnableGunField = {
        pileId: Number(pileId),
        gunNumbers: [currentGunIndexString],
        disableStartTime: 0,
        disableFinishTime: 0,
      };
      try {
        const res = await api.post(API.ENABLE_GUN, payload);
        const { code, msg }: CommonResponseObj = res.data;
        if (code !== 0) {
          message.error(msg || t('Common.OperationFailed'));
          setIsDisableOrEnableLoading(false);
          return;
        }
        message.success(t('Common.OperationSuccess'));
        getPileDetail();
      } catch (err) {
        console.warn(err);
        message.error(t('Common.OperationFailed'));
      }
    } else {
      // 禁用

      const payload: DisableEnableGunField = {
        disableId: null,
        pileId: Number(pileId),
        gunNumbers: [currentGunIndexString],
        disableStartTime: 0,
        disableFinishTime: 0,
      };
      try {
        const res = await api.post(API.DISABLE_GUN, payload);
        const { code, msg }: CommonResponseObj = res.data;
        if (code !== 0) {
          message.error(msg || t('Common.OperationFailed'));
          setIsDisableOrEnableLoading(false);
          return;
        }
        message.success(t('Common.OperationSuccess'));
        getPileDetail();
      } catch (err) {
        console.warn(err);
        message.error(t('Common.OperationFailed'));
      }
    }
    setIsDisableOrEnableLoading(false);
  }

  async function handleUnlockGun() {
    const payload = {
      pileId: Number(pileId),
      gunNumbers: [currentGunIndexString],
    };
    setIsUnlockLoading(true);

    try {
      await checkGunCanEdit();

      const res = await api.post(API.UNLOCK_GUN, payload);
      const { code, msg }: CommonResponseObj = res.data;
      if (code !== 0) {
        message.error(msg || t('Common.OperationFailed'));
        setIsUnlockLoading(false);
        return;
      }
      message.success(t('Common.OperationSuccess'));
      getPileDetail();
    } catch (err) {
      console.warn(err);
    }
    setIsUnlockLoading(false);
  }

  async function resetPile(type: 'HARD' | 'SOFT') {
    setIsResetLoading(true);
    try {
      const API_URL =
        type === 'HARD' ? API.HARD_RESET_PILE : API.SOFT_RESET_PILE;
      const res = await api.post(API_URL, {
        pileId: Number(pileId),
      });
      const { code, msg }: CommonResponseObj = res.data;
      if (code !== 0) {
        message.error(msg || t('Common.OperationFailed'));
        setIsResetLoading(false);
        return;
      }
      message.success(t('Common.OperationSuccess'));
      getPileDetail();
    } catch (err) {
      console.warn(err);
    }
    setIsResetLoading(false);
  }

  function checkIsPileResetDisallowed(pileTypeCatalog: number) {
    // hard override: 如果現在是 engMode 的話，就允許遠端重置
    if (localStorage.getItem('engMode') === 'true') return false;

    // edge case: 如果無法辨識 pileTypeCatalog 的話，就禁止遠端重置
    if (pileTypeCatalog === -1) {
      console.warn(
        '無法辨識 pileTypeCatalog，禁止遠端重置。pileData:',
        pileData,
      );
      return true;
    }

    // 如果樁中有任一充電槍預約中，則禁止遠端重置
    if (
      pileData.gunInfos?.some((gun) => gun.operationViewStatus === 'Reserved')
    ) {
      return true;
    }

    // 如果該樁為 eTreego (45) 且有任意充電槍正在充電中，則禁止遠端重置
    if (
      pileTypeCatalog === 45 &&
      pileData.gunInfos?.some((gun) => gun.operationViewStatus === 'Charging')
    ) {
      return true;
    }

    return false;
  }

  // Update the ref whenever the page number changes
  useEffect(() => {
    alertListCurrentRef.current = alertListPageConfig.current;
  }, [alertListPageConfig.current]);

  useEffect(() => {
    getPileDetail();
    getAlertList();

    return () => {
      clearTimeout(pileDetailTimeoutRef.current);
      clearTimeout(alertListTimeoutRef.current);

      pileDetailAbortRef.current?.abort();
      alertListAbortRef.current?.abort();
    };
  }, []);

  const pileStatusColorEnum = {
    Offline: token.GunMaintenanceStatusColors.OFFLINE,
    Normal: token.GunMaintenanceStatusColors.CHARGING,
    Abnormal: token.GunMaintenanceStatusColors.UNAVAILABLE,
    Unexpected_NoGuns: token.colorError,
  };

  function getRemoteEnableOrDisableOptionConfig() {
    switch (currentGun?.connectorStatus) {
      case 'Available':
        return {
          text: t('RealTimePileDetail.DisableGun'),
          icon: <CloseCircleOutlined />,
          confirmText: t('RealTimePileDetail.ConfirmDisableGun'),
        };
      case 'Unavailable':
        return {
          text: t('RealTimePileDetail.EnableGun'),
          icon: <CheckCircleOutlined />,
          confirmText: t('RealTimePileDetail.ConfirmEnableGun'),
        };

      default:
        return {
          text: t('RealTimePileDetail.DisableGun'),
          icon: <QuestionCircleOutlined />,
        };
    }
  }
  const remoteEnableOrDisableOptionConfig =
    getRemoteEnableOrDisableOptionConfig();

  const remoteControlGunMenuItems: MenuItem[] = [
    {
      label: remoteEnableOrDisableOptionConfig.text,
      key: '1',
      icon: remoteEnableOrDisableOptionConfig.icon,
      disabled:
        currentGun?.connectorStatus !== 'Available' &&
        currentGun?.connectorStatus !== 'Unavailable',

      onClick: () => {
        confirmDisableOrEnable({
          title: remoteEnableOrDisableOptionConfig.text,
          content: remoteEnableOrDisableOptionConfig.confirmText,
          confirmLoading: isDisableOrEnableLoading,
          onOk: () => {
            handleDisableOrEnableGun();
          },
        });
      },
    },
  ];
  if (localStorage.getItem('engMode') === 'true') {
    remoteControlGunMenuItems.push({
      label: t('RealTimePileDetail.UnlockGunHead'),
      key: '2',
      icon: <UnlockOutlined />,
      onClick: () => {
        confirmUnlock({
          title: t('RealTimePileDetail.UnlockGunHead'),
          content: t('RealTimePileDetail.ConfirmUnlockGunHead'),
          confirmLoading: isUnlockLoading,
          onOk: () => {
            handleUnlockGun();
          },
        });
      },
    });
  }

  const partNumberSeriesName = getPartNumberSeriesName(
    pileData.pileMetadataVo?.pileTypeName,
  );

  const isPileResetDisallowed = checkIsPileResetDisallowed(
    pileData.pileMetadataVo?.pileTypeCatalog || -1,
  );

  return (
    <div className="RealtimePileMonitor">
      <TitleBar title={t('RealTimePileDetail.PileDetails')} />
      {/* 基本資訊 */}
      <Card>
        <div className="RealtimePileMonitor__titleBar">
          <div className="RealtimePileMonitor__title">
            {t('Common.BasicInfo')}
          </div>
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                {
                  label: t('RealTimePileDetail.StopReturnStandby'),
                  key: '1',
                  icon: <ReloadOutlined />,
                  disabled: isResetLoading || isPileResetDisallowed,
                  onClick: () => {
                    confirmSoftReset({
                      title: t('RealTimePileDetail.StopReturnStandby'),
                      content: t('RealTimePileDetail.ConfirmStopReturnStandby'),
                      confirmLoading: isResetLoading,
                      onOk: () => {
                        resetPile('SOFT');
                      },
                    });
                  },
                },
                {
                  label: t('RealTimePileDetail.StopRestart'),
                  key: '2',
                  icon: <PoweroffOutlined />,
                  disabled: isResetLoading || isPileResetDisallowed,
                  onClick: () => {
                    confirmHardReset({
                      title: t('RealTimePileDetail.StopRestart'),
                      content: t('RealTimePileDetail.ConfirmStopRestart'),
                      confirmLoading: isResetLoading,
                      onOk: () => {
                        resetPile('HARD');
                      },
                    });
                  },
                },
              ],
            }}
            className="RealtimePileMonitor__gunDropdown"
          >
            <Button>
              {t('RealTimePileDetail.RemoteControlPile')}
              <CaretDownOutlined />
            </Button>
          </Dropdown>
        </div>
        <div className="RealtimePileMonitor__pileInfos">
          {/* 充電樁名稱 */}
          <div className="RealtimePileMonitor__infoItem">
            <div className="RealtimePileMonitor__infoTitle">
              {t('Common.PileName')}
            </div>
            <div className="RealtimePileMonitor__infoContent">
              <Popover content={pileData.pileMetadataVo?.pileName || ''}>
                {pileData.pileMetadataVo?.pileName || '-'}
              </Popover>
            </div>
          </div>
          {/* 所屬充電站 */}
          <div className="RealtimePileMonitor__infoItem">
            <div className="RealtimePileMonitor__infoTitle">
              {t('RealTimePileDetail.BelongingStation')}
            </div>
            <div className="RealtimePileMonitor__infoContent">
              <Popover content={pileData.pileMetadataVo?.stationName || '-'}>
                {pileData.pileMetadataVo?.stationName || '-'}
              </Popover>
            </div>
          </div>
          {/* 硬體品牌 */}
          <div className="RealtimePileMonitor__infoItem">
            <div className="RealtimePileMonitor__infoTitle">
              {t('RealTimePileDetail.HardwareBrand')}
            </div>
            <div className="RealtimePileMonitor__infoContent">
              <Tag>
                {CATALOG[pileData.pileMetadataVo?.pileTypeCatalog || -1] || '-'}
              </Tag>
            </div>
          </div>
          {/* 硬體型號 */}
          <div className="RealtimePileMonitor__infoItem">
            <div className="RealtimePileMonitor__infoTitle">
              {t('RealTimePileDetail.HardwareModel')}
            </div>
            <div className="RealtimePileMonitor__infoContent">
              <Popover content={partNumberSeriesName}>
                <Tag>{partNumberSeriesName}</Tag>
              </Popover>
            </div>
          </div>
          {/* 充電樁充電效率類型 */}
          <div className="RealtimePileMonitor__infoItem">
            <div className="RealtimePileMonitor__infoTitle">
              {t('Common.PileEfficiencyType')}
            </div>
            <div className="RealtimePileMonitor__infoContent">
              <Tag>{pileData.pileMetadataVo?.currentMode || '-'}</Tag>
            </div>
          </div>
          {/* 充電樁額定功率 */}
          <div className="RealtimePileMonitor__infoItem">
            <div className="RealtimePileMonitor__infoTitle">
              {t('RealTimePileDetail.PileRatedPower')}
            </div>
            <div className="RealtimePileMonitor__infoContent">
              {formatNumberWithDigits(
                pileData.pileMetadataVo?.ratingPower ?? 0,
                0,
              )}
            </div>
          </div>
          {/* 平台識別充電樁硬體編號 */}
          <div className="RealtimePileMonitor__infoItem">
            <div className="RealtimePileMonitor__infoTitle">
              {t('Common.PlatformPileHardwareNum')}
            </div>
            <div className="RealtimePileMonitor__infoContent">
              <Popover content={pileData.pileMetadataVo?.pileNo || '-'}>
                {pileData.pileMetadataVo?.pileNo || '-'}
              </Popover>
            </div>
          </div>
          {/* 充電樁運行狀態 */}
          <div className="RealtimePileMonitor__infoItem">
            <div className="RealtimePileMonitor__infoTitle">
              {t('RealTimePileDetail.PileOperationalStatus')}
            </div>
            <div
              className="RealtimePileMonitor__infoContent"
              style={{
                color:
                  pileStatusColorEnum[
                    (pileData.pileMetadataVo?.pileStatus ||
                      '') as keyof typeof pileStatusColorEnum
                  ],
              }}
            >
              {t(
                `Enum.${
                  PileStatusEnum[
                    pileData.pileMetadataVo
                      ?.pileStatus as keyof typeof PileStatusEnum
                  ]
                }`,
              ) || '-'}
            </div>
          </div>
          {/* 充電槍數量 */}
          <div className="RealtimePileMonitor__infoItem">
            <div className="RealtimePileMonitor__infoTitle">
              {t('Common.GunCount')}
            </div>
            <div className="RealtimePileMonitor__infoContent">
              {formatNumberWithDigits(
                pileData.pileMetadataVo?.gunCount ?? 0,
                0,
              )}
            </div>
          </div>
        </div>
      </Card>
      {/* 即時充電儀表板 */}
      <Card>
        <div className="RealtimePileMonitor__title realtimeDashboardTitle">
          {t('RealTimePileDetail.RealTimeDashboard')}
        </div>
        {pileData.gunInfos?.length &&
        pileData.pileStatus !== 'Unexpected_NoGuns' ? (
          <>
            <Menu
              className="RealtimePileMonitor__gunTabs"
              selectedKeys={
                gunData.currentGunId ? [gunData.currentGunId.toString()] : []
              }
              items={generateGunTabs()}
              onClick={(e) => {
                onChangeViewingGun(e.key);
              }}
              mode="horizontal"
            />
            <div className="RealtimePileMonitor__gunDetailWrapper">
              <div className="RealtimePileMonitor__gunInfos">
                <div className="RealtimePileMonitor__infoItem">
                  <div className="RealtimePileMonitor__infoTitle">
                    {t('Common.GunName')}
                  </div>
                  <div className="RealtimePileMonitor__infoContent">
                    <Popover content={currentGun?.gunName || '-'}>
                      {currentGun?.gunName || '-'}
                    </Popover>
                  </div>
                </div>
                <div className="RealtimePileMonitor__infoItem">
                  <div className="RealtimePileMonitor__infoTitle">
                    {t('Common.ParkingSpace')}
                  </div>
                  <div className="RealtimePileMonitor__infoContent">
                    <Popover content={currentGun?.parkingLotName || '-'}>
                      {currentGun?.parkingLotName || '-'}
                    </Popover>
                  </div>
                </div>
                <div className="RealtimePileMonitor__infoItem">
                  <div className="RealtimePileMonitor__infoTitle">
                    {t('Common.GunSocketSpec')}
                  </div>
                  <div className="RealtimePileMonitor__infoContent">
                    <Tag>{currentGun?.socketType || '-'}</Tag>
                  </div>
                </div>
                <div className="RealtimePileMonitor__infoItem">
                  <div
                    className="RealtimePileMonitor__infoTitle"
                    style={{ flexShrink: 0 }}
                  >
                    {t('RealTimePileDetail.GunOperatingStatus')}
                  </div>
                  <div
                    className="RealtimePileMonitor__infoContent"
                    style={{
                      color:
                        token.GunMaintenanceStatusColors[
                          currentGun?.connectorStatus?.toUpperCase() || ''
                        ],
                    }}
                  >
                    <Popover
                      content={t(
                        `Enum.${
                          ConnectorStatusEnum[
                            currentGun?.connectorStatus || 'Unexpected'
                          ]
                        }`,
                      )}
                    >
                      {t(
                        `Enum.${
                          ConnectorStatusEnum[
                            currentGun?.connectorStatus || 'Unexpected'
                          ]
                        }`,
                      )}
                    </Popover>
                  </div>
                </div>
                <div className="RealtimePileMonitor__infoItem">
                  <div className="RealtimePileMonitor__infoTitle">
                    {t('RealTimePileDetail.TimeCharged')}
                  </div>
                  <div className="RealtimePileMonitor__infoContent">
                    {formatDuration(
                      currentGun?.transactionalData?.duration || 0,
                    )}
                  </div>
                </div>
                <div className="RealtimePileMonitor__infoItem">
                  <div className="RealtimePileMonitor__infoTitle">
                    {t('RealTimePileDetail.StartMethod')}
                  </div>
                  <div className="RealtimePileMonitor__infoContent">
                    {currentGun?.transactionalData?.startChargeType !==
                    undefined
                      ? t(
                          `Enum.${
                            StartChargeTypeEnum[
                              currentGun.transactionalData
                                .startChargeType as keyof typeof StartChargeTypeEnum
                            ]
                          }`,
                        )
                      : '-'}
                  </div>
                </div>
              </div>

              {/* <Dropdown
                trigger={['click']}
                menu={{
                  items: remoteControlGunMenuItems,
                }}
                className="RealtimePileMonitor__gunDropdown"
              >
                <Popover content="目前平台僅允許充電槍於連線狀態且處於空閒中時，可以下發遠程控制命令，如果遇到充電槍離線或充電中，請稍後再試">
                  <Button>
                    充電槍遠端控制
                    <CaretDownOutlined />
                    <QuestionCircleOutlined />
                  </Button>
                </Popover>
              </Dropdown> */}

              <span className="RealtimePileMonitor__pileRemoteControl">
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: remoteControlGunMenuItems,
                  }}
                  className="RealtimePileMonitor__gunDropdown"
                >
                  <Button>
                    {t('RealTimePileDetail.RemoteControlPile')}
                    <CaretDownOutlined />
                  </Button>
                </Dropdown>
                <Popover
                  content={t('RealTimePileDetail.RemoteControlLimit')}
                  overlayStyle={{ maxWidth: '30%' }}
                >
                  <QuestionCircleOutlined />
                </Popover>
              </span>
            </div>
            <div className="RealtimePileMonitor__charts">
              {/* 即時電流 */}
              <div className="RealtimePileMonitor__gaugeChart">
                <div className="RealtimePileMonitor__gaugeOverlay ">
                  {t('RealTimePileDetail.RealTimeCurrent')}
                </div>
                <GaugeChart
                  data={{
                    maxValue:
                      pileData?.pileMetadataVo?.currentMode === 'DC'
                        ? 500
                        : 100,
                    value: Number(
                      formatNumberWithDigits(
                        currentGun?.transactionalData?.current ?? 0,
                        3,
                      ),
                    ),
                  }}
                />
              </div>
              {/* 即時功率 */}
              <div className="RealtimePileMonitor__progressChart">
                <div className="RealtimePileMonitor__progressOverlay">
                  {pileData?.pileMetadataVo?.currentMode === 'DC' && (
                    <div className="soc">
                      {formatNumberWithDigits(
                        currentGun?.transactionalData?.soc || 0,
                        0,
                      )}
                      %
                    </div>
                  )}

                  <div className="label">
                    {t('RealTimePileDetail.ChargedEnergy')}
                  </div>
                  <div className="energyOffered">
                    {formatNumberWithDigits(
                      currentGun?.transactionalData?.energyOffered || 0,
                      3,
                    )}{' '}
                    kWh
                  </div>
                </div>
                {/* 只有在 currentMode === 'DC' 時顯示 */}
                {pileData?.pileMetadataVo?.currentMode === 'DC' && (
                  <SocChart
                    percentage={currentGun?.transactionalData?.soc || 0}
                    color={token.colorSuccess}
                  />
                )}
              </div>
              {/* 即時電壓 */}
              <div className="RealtimePileMonitor__gaugeChart">
                <div className="RealtimePileMonitor__gaugeOverlay ">
                  {t('RealTimePileDetail.RealTimeVoltage')}
                </div>
                <GaugeChart
                  data={{
                    maxValue:
                      pileData?.pileMetadataVo?.currentMode === 'DC'
                        ? 1200
                        : 300,
                    value: Number(
                      formatNumberWithDigits(
                        currentGun?.transactionalData?.voltage ?? 0,
                        3,
                      ),
                    ),
                  }}
                />
              </div>
            </div>
          </>
        ) : (
          <Text className="RealtimePileMonitor__noGun">
            {t('Common.NoData')}
          </Text>
        )}
      </Card>
      {/* 即時警示摘要資訊 */}
      <Card>
        <div className="RealtimePileMonitor__title">
          {t('RealTimePileDetail.RealTimeSummary')}
        </div>
        <CustomTable<AlertList>
          className="RealtimePileMonitor__alertTable"
          isLoading={isAlertListLoading}
          columns={alertColumns}
          dataSource={alertList}
          scroll={{ x: 1164 }}
          rowKey="id"
          paginationConfig={{
            current: alertListPageConfig.current,
            onChange: (e) => {
              getAlertList(e);
            },
            total: alertListPageConfig.total,
          }}
        />
      </Card>
    </div>
  );
}

export default RealTimePileDetail;
